import { getCheckoutForSite, activeSiteCountry } from "@config/site/site-config";
import { isProduction } from "@config/site/site-api-settings";
import Checkout from "@lib/constants/contentful/checkout";
import CommonCMS from "@lib/constants/contentful/cms-common";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { useStore } from "@store/index";
import {
    useAddShippingMethodToCart,
    useUpdateBillingAddress,
    useUpdateDeliveryAddress,
    useUpdateDeliveryDateAndTimeSlot,
} from "@store/hooks";
import type DefaultAddressFields from "@interfaces/address/ExtendedAddressFields";
import type { CustomerAddressesResponse } from "@interfaces/address/ExtendedAddressFields";
import { useMoney } from "@ui/components/data-display/money/useMoney";
import { useRouter } from "next/router";
import type { Dispatch, SetStateAction } from "react";
import { useState } from "react";
import { initializeFormValues as initializeFormValuesFn } from "../checkoutHelper";
import { countryCodeLookup } from "../countryUtility";
import { getAdyenCheckout } from "./adyenPayment";
import type { Cart } from "@graphql/generated/components";
import { executeWithRetry } from "../executeWithRetry";
import { FetchServiceAxios } from "@lib/helpers/fetchServiceAxios";
import { dateSelectorEnabledDeliveryMethods } from "@lib/constants/dateSelectorDeliveryMethods";
import { fullNameParser } from "@lib/validators/regex";
import { isCountryWithReverseNameOrder } from "../address";
import type { TCountryCode } from "countries-list";

type CheckoutState = {
    errors?: {
        contactDetails?: string;
        delivery?: string;
    };
};

type CheckoutErrors = {
    contactDetails: string;
    delivery: string;
};

export type CheckoutFns = {
    countryCode?: string;
    country?: string;
    locale?: string;
};

const getDefaultAddress =
    ({ setDefaultAddress, getCart }) =>
    async () => {
        const cart = getCart();
        if (!cart) return;

        const { addresses } = await FetchServiceAxios.get<CustomerAddressesResponse>(
            `/api/addresses/get-addresses`
        );

        const { fullName, additionalAddressInfo, city, country, streetName, region, postalCode } =
            addresses.find((address) => address.isDefault);
        setDefaultAddress({
            fullName,
            additionalAddressInfo,
            city,
            country,
            streetName,
            postalCode,
            region,
        });
    };

// We are saving a full name in my-addresses and receiving first and last name for checkout.
export const separateFullName = (fullName: string, countryCode: TCountryCode) => {
    const nameParts = fullName?.trim()?.match(fullNameParser);

    if (!nameParts || nameParts.length < 2) {
        return [fullName, fullName];
    }

    const isReversed = isCountryWithReverseNameOrder(countryCode);

    const firstName = isReversed ? nameParts.slice(1).join(" ") : nameParts.slice(0, -1).join(" ");
    const lastName = isReversed ? nameParts[0] : nameParts[nameParts.length - 1];

    return [firstName, lastName];
};

type addContactDetailsProps = {
    getCart: () => Cart;
    locale: string;
    fns: any;
    errorMessage: string;
    setState: Dispatch<SetStateAction<CheckoutState>>;
};

const addContactDetails = ({
    getCart,
    locale,
    setState,
    errorMessage,
    fns,
}: addContactDetailsProps) => {
    const { executeUpdateDeliveryAddress } = fns;
    return async (values) => {
        const cart = getCart();
        const { error } = await executeWithRetry(
            executeUpdateDeliveryAddress,
            {
                id: cart.id,
                version: cart.version,
                storeKey: cart.store.key,
                locale,
                ...cart.shippingAddress,
                ...values,
            },
            0,
            "Adding Contact Details"
        );
        if (error) {
            setState({
                errors: {
                    contactDetails: isProduction
                        ? errorMessage
                        : JSON.stringify(error.graphQLErrors.map((e) => e.message)),
                },
            });
            return { errors: true };
        }
        return { errors: null };
    };
};

const addDeliveryMethod = ({
    getCart,
    locale,
    setState,
    errorMessage,
    fns,
}: addContactDetailsProps) => {
    const { executeAddShippingMethodToCart } = fns;
    return async (deliveryMethodId: string) => {
        const cart = getCart();
        const { error } = await executeWithRetry(
            executeAddShippingMethodToCart,
            {
                id: cart.id,
                version: cart.version,
                storeKey: cart.store.key,
                locale,
                // At the moment we have only two delivery methods, so if there is CC, will be click and collect
                shippingMethod: deliveryMethodId,
            },
            0,
            "Adding Delivery Method"
        );
        if (error) {
            setState({
                errors: {
                    contactDetails: isProduction ? errorMessage : JSON.stringify(error),
                },
            });
            return { errors: true };
        }
        return { errors: null };
    };
};

const addDeliveryAddress = ({ getCart, locale, setState, errorMessage, fns }) => {
    const { executeUpdateDeliveryAddress } = fns;
    return async (values) => {
        const cart = getCart();
        if (values.country) {
            values.country = await countryCodeLookup(values.country);
        }
        const { error } = await executeWithRetry(
            executeUpdateDeliveryAddress,
            {
                id: cart.id,
                version: cart.version,
                storeKey: cart.store.key,
                locale,
                ...cart.shippingAddress,
                ...values,
            },
            0,
            "Adding Delivery Address"
        );

        if (error) {
            setState({
                errors: {
                    delivery: isProduction ? errorMessage : JSON.stringify(error),
                },
            });
            return { errors: true };
        }

        return { errors: null };
    };
};

const addDeliveryDateAndTimeSlot = ({ getCart, locale, setState, errorMessage, fns }) => {
    const { executeUpdateDeliveryDateAndTimeSlot } = fns;
    return async (values) => {
        const cart = getCart();
        const isCustomFieldAlreadyExistsInCart = (cart: Cart, fieldName: string) => {
            return cart?.custom?.customFieldsRaw?.find((item) => item.name === fieldName)?.value;
        };

        const isDeliveryDateAlreadyExists = isCustomFieldAlreadyExistsInCart(
            cart,
            "order_CF_DeliveryDate"
        );
        const isDeliveryTimeAlreadyExists = isCustomFieldAlreadyExistsInCart(
            cart,
            "order_CF_DeliveryTimeSlot"
        );

        if (
            (!isDeliveryDateAlreadyExists && !values.date) ||
            (!isDeliveryTimeAlreadyExists && !values.timeslot)
        ) {
            return { errors: null };
        }

        const isActiveCountryWithDateSelector =
            activeSiteCountry in dateSelectorEnabledDeliveryMethods;

        if (!values.date && !values.timeslot && !isActiveCountryWithDateSelector)
            return { errors: null };

        const { error } = await executeWithRetry(
            executeUpdateDeliveryDateAndTimeSlot,
            {
                id: cart.id,
                version: cart.version,
                storeKey: cart.store.key,
                locale,
                date: JSON.stringify(values.date),
                timeslot: JSON.stringify(values.timeslot),
            },
            0,
            "Adding Delivery Date and Time Slot"
        );

        if (error) {
            setState({
                errors: {
                    delivery: isProduction ? errorMessage : JSON.stringify(error),
                },
            });
            return { errors: true };
        }

        return { errors: null };
    };
};

const addBillingAddress = ({ getCart, locale, setState, errorMessage, fns }) => {
    const { executeUpdateBillingAddress } = fns;
    return async (values) => {
        const cart = getCart();
        const oldCountry = values.country;
        const payloadValues = structuredClone(values);
        if (values.country) {
            payloadValues.country = await countryCodeLookup(values.country);
        }

        if (!values.country) {
            setState({
                errors: {
                    delivery: `failed to save country by name ${oldCountry}.`,
                },
            });
            return { errors: true };
        }
        const { error } = await executeWithRetry(
            executeUpdateBillingAddress,
            {
                id: cart.id,
                version: cart.version,
                storeKey: cart.store.key,
                locale,
                ...cart.billingAddress,
                ...payloadValues,
            },
            0,
            "Adding Billing Address"
        );
        if (error) {
            setState({
                errors: {
                    delivery: isProduction ? errorMessage : JSON.stringify(error),
                },
            });
            return { errors: true };
        }

        return { errors: null };
    };
};

const initializeFormValuesHook = ({ getCart, locale, storeData }) => {
    return () => {
        const cart = getCart();
        const country = activeSiteCountry;
        const { countryCode } = getCheckoutForSite;

        const variables: CheckoutFns = {
            country,
            countryCode,
            locale,
        };

        return initializeFormValuesFn(cart, variables, storeData);
    };
};

export const useCheckout = () => {
    const [state, setState] = useState<CheckoutState>({});
    const [defaultAddress, setDefaultAddress] = useState<DefaultAddressFields>(null);
    const router = useRouter();
    const { locale } = router;
    const { get: getMicrocopy } = useMicrocopy();
    const executeUpdateDeliveryAddress = useUpdateDeliveryAddress();
    const executeUpdateDeliveryDateAndTimeSlot = useUpdateDeliveryDateAndTimeSlot();
    const executeAddShippingMethodToCart = useAddShippingMethodToCart();
    const executeUpdateBillingAddress = useUpdateBillingAddress();
    const storeData = useStore((state) => state.data.storeData);
    const cart = useStore((state) => state.data.me.cartInfo.cart);
    const getCart = useStore((state) => state.getCart);
    const masterProducts = useStore((state) => state.data.me.cartInfo.masterProducts);

    const money = useMoney({
        locale,
        money: cart?.totalPrice,
    });

    const errors: CheckoutErrors = {
        contactDetails: getMicrocopy(CommonCMS.page, Checkout.step1.contactDetails.error),
        delivery: getMicrocopy(CommonCMS.page, Checkout.step1.delivery.error),
    };

    return {
        addContactDetails: addContactDetails({
            getCart,
            locale,
            setState,
            errorMessage: errors.contactDetails,
            fns: { executeUpdateDeliveryAddress },
        }),
        addDeliveryAddress: addDeliveryAddress({
            getCart,
            locale,
            setState,
            errorMessage: errors.delivery,
            fns: { executeUpdateDeliveryAddress },
        }),
        addDeliveryDateAndTimeSlot: addDeliveryDateAndTimeSlot({
            getCart,
            locale,
            setState,
            errorMessage: errors.delivery,
            fns: { executeUpdateDeliveryDateAndTimeSlot },
        }),
        addBillingAddress: addBillingAddress({
            getCart,
            locale,
            setState,
            errorMessage: errors.delivery,
            fns: { executeUpdateBillingAddress },
        }),
        addDeliveryMethod: addDeliveryMethod({
            getCart,
            locale,
            setState,
            errorMessage: errors.delivery,
            fns: { executeAddShippingMethodToCart },
        }),
        getAdyenCheckout: getAdyenCheckout({
            money,
            router,
        }),
        initializeFormValues: initializeFormValuesHook({ getCart, locale, storeData }),
        cart,
        masterProducts,
        state,
        getDefaultAddress: getDefaultAddress({ getCart, setDefaultAddress }),
        defaultAddress,
    };
};
